import React from "react";
import {
  Phone, Check, Shield, Star, Smile, Home, Heart,
} from "lucide-react";
import FlyerPage from "./FlyerPage";
import FlyerLogo from "./FlyerLogo";

const HELP_WITH = [
  "Setting up a new phone, iPad or PC",
  "Showing you how to video-call family",
  "Sorting your email and passwords",
  "Removing scam pop-ups and warnings",
  "Checking if a phone call was a scam",
  "MyGov, banking and Medicare online",
  "Backing up your photos safely",
  "Wi-Fi, printers and email",
];

const QR_URL =
  "https://api.qrserver.com/v1/create-qr-code/?data=https%3A%2F%2Ftechemergency.com.au%2F&size=240x240&margin=0&color=050505&bgcolor=ffffff&qzone=1";

export default function FlyerSeniors() {
  return (
    <FlyerPage title="Flyer — Adelaide Seniors" testid="flyer-seniors">
      {/* === Header: solid red wedge with logo === */}
      <div className="relative -mx-[12mm] -mt-[14mm] mb-5">
        <div
          className="px-[12mm] pt-[12mm] pb-7 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, var(--te-primary) 0%, #d12826 100%)",
          }}
        >
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 1px, transparent 1px 8px)",
            }}
          />
          <div className="relative flex items-center justify-between gap-3">
            <FlyerLogo size="lg" />
            <div className="flex items-center gap-1 text-[9.5px] font-bold tracking-[0.18em] uppercase text-white/90 bg-black/30 px-2 py-1">
              <Star size={9} fill="#fff" stroke="#fff" />
              TRUSTED LOCALS
            </div>
          </div>
        </div>
        <svg
          viewBox="0 0 148 8"
          preserveAspectRatio="none"
          className="block w-full h-3"
          style={{ marginTop: "-1px" }}
        >
          <polygon points="0,0 148,0 148,2 0,8" fill="#d12826" />
        </svg>
      </div>

      {/* === Headline === */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-block w-5 h-[2px]"
            style={{ background: "var(--te-primary)" }}
          />
          <span className="text-[9.5px] tracking-[0.3em] font-bold uppercase text-zinc-400">
            For Adelaide Seniors
          </span>
        </div>
        <h1 className="text-[28px] font-black leading-[1.0] tracking-tight">
          Need a hand<br />
          with your{" "}
          <span style={{ color: "var(--te-primary)" }}>computer</span>{" "}
          or{" "}
          <span style={{ color: "var(--te-primary)" }}>phone?</span>
        </h1>
      </div>

      <p className="text-[14px] text-white leading-snug mb-3 font-semibold">
        We come to you. No rushing. No jargon.
      </p>
      <p className="text-[12px] text-zinc-300 leading-relaxed mb-4">
        A small Adelaide team that helps older locals with everyday technology —
        patiently, in your own home, at your own pace.
      </p>

      {/* === Reassurance band === */}
      <div
        className="relative mb-4 px-4 py-3"
        style={{
          background: "rgba(255,59,48,0.12)",
          borderLeft: "3px solid var(--te-primary)",
        }}
      >
        <div className="flex items-start gap-2.5">
          <Smile
            size={20}
            style={{ color: "var(--te-primary)", flexShrink: 0, marginTop: 1 }}
          />
          <div>
            <div className="text-[13.5px] font-bold mb-0.5 leading-tight">
              No question is too small.
            </div>
            <div className="text-[11.5px] text-zinc-300 leading-snug">
              No problem is &ldquo;stupid&rdquo;. We explain everything as we go.
            </div>
          </div>
        </div>
      </div>

      {/* === Help with === */}
      <div className="mb-4">
        <div className="text-[9.5px] tracking-[0.24em] font-bold uppercase text-zinc-500 mb-2 flex items-center gap-2">
          <span
            className="w-3 h-[2px]"
            style={{ background: "var(--te-primary)" }}
          />
          We can help with
        </div>
        <ul className="space-y-1.5">
          {HELP_WITH.map((f) => (
            <li key={f} className="flex items-start gap-2 text-[12px] leading-snug">
              <Check
                size={11}
                strokeWidth={3}
                style={{ marginTop: 2, color: "var(--te-primary)" }}
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* === Trust band (Important for seniors) === */}
      <div
        className="relative mb-4 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,59,48,0.14) 0%, rgba(255,59,48,0.04) 100%)",
          border: "1px solid rgba(255,59,48,0.4)",
        }}
      >
        <div
          className="absolute -right-3 -top-3 opacity-10 select-none"
          style={{ color: "var(--te-primary)" }}
        >
          <Shield size={72} strokeWidth={1.5} />
        </div>
        <div className="relative px-4 py-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Shield size={12} style={{ color: "var(--te-primary)" }} />
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase">
              Important for seniors
            </span>
          </div>
          <ul className="space-y-0.5 text-[11.5px] text-zinc-200 leading-snug">
            <li>
              <strong
                className="text-[14px]"
                style={{ color: "var(--te-primary)" }}
              >
                $0
              </strong>{" "}
              to come to your home — no surprise fees.
            </li>
            <li>No upselling. No technical jargon.</li>
            <li>Police-checked, friendly technicians.</li>
            <li>If we can fix it on the phone — we will. No visit needed.</li>
          </ul>
        </div>
      </div>

      {/* === Phone CTA + QR === */}
      <div className="grid grid-cols-[1fr_auto] gap-3 mb-3 items-stretch">
        <div
          className="px-4 py-3 flex flex-col justify-center"
          style={{ background: "var(--te-primary)" }}
        >
          <div className="text-[9px] tracking-[0.24em] font-bold uppercase opacity-90 mb-1">
            / Happy to chat anytime
          </div>
          <div className="flex items-center gap-1.5 mb-1">
            <Phone size={20} strokeWidth={2.8} />
            <span className="text-[26px] font-black tracking-tight leading-none">
              1800 836 390
            </span>
          </div>
          <div className="text-[10px] leading-snug opacity-90">
            Talk you through any worry — even before you book.
          </div>
        </div>
        <div className="bg-white p-1.5 flex flex-col items-center justify-center w-[68px]">
          <img
            src={QR_URL}
            alt="Scan to visit techemergency.com.au"
            className="w-[55px] h-[55px]"
            crossOrigin="anonymous"
          />
          <div className="text-[6.5px] font-bold tracking-[0.12em] uppercase text-black mt-0.5 text-center leading-tight">
            Scan me
          </div>
        </div>
      </div>

      <div className="text-[8.5px] text-center text-zinc-500 leading-snug tracking-[0.08em] uppercase border-t border-white/10 pt-2.5 flex items-center justify-center gap-1.5">
        <Heart size={9} style={{ color: "var(--te-primary)" }} />
        Adelaide-wide · Trusted local team · techemergency.com.au
      </div>
    </FlyerPage>
  );
}
